import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Palette, GripVertical } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import ColorCollectionFormSlider from '../components/ColorCollectionFormSlider';
import ColorCollectionDetailsSlider from '../components/ColorCollectionDetailsSlider';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface ColorCollection {
  _id: string;
  name: string;
  description?: string;
  projectId: string;
  projectName?: string;
  isActive: boolean;
  order: number;
  heroImageId?: string;
  heroImageUrl?: string;
  photoIds?: string[];
  itemCategories?: any[];
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const ColorCollections: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get slider and drawer state from URL params
  const viewColorCollectionId = searchParams.get('view');
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editColorCollectionId = searchParams.get('edit');

  const [colorCollections, setColorCollections] = useState<ColorCollection[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Set default project filter to first project
  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Set first project as default when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !projectFilter) {
      setProjectFilter(projects[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    if (workspaceId && projectFilter) {
      fetchColorCollections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, searchQuery, projectFilter]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchColorCollections = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          projectId: projectFilter || undefined,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });

      setColorCollections(response.data.colorCollections);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch color collections');
      console.error('Error fetching color collections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    // Reset to first project instead of empty
    if (projects.length > 0) {
      setProjectFilter(projects[0]._id);
    }
    setPagination({ ...pagination, page: 1 });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder collections array
    const newCollections = [...colorCollections];
    const [draggedCollection] = newCollections.splice(draggedIndex, 1);
    newCollections.splice(dropIndex, 0, draggedCollection);

    // Update local state immediately for better UX
    setColorCollections(newCollections);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Update order in backend
    try {
      await Promise.all(
        newCollections.map((collection, index) =>
          api.put(
            `/workspaces/${workspaceId}/pkg-color-collections/${collection._id}`,
            { order: index },
          )
        )
      );
    } catch (err: any) {
      console.error('Error updating color collection order:', err);
      // Revert on error by refetching
      fetchColorCollections();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleCreateColorCollection = () => {
    setSearchParams({ action: 'new' });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchColorCollections();
  };

  const handleViewColorCollection = (id: string) => {
    setSearchParams({ view: id });
  };

  const handleCloseSlider = () => {
    setSearchParams({});
  };

  const handleSaveColorCollection = () => {
    fetchColorCollections();
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Color Collections</h2>
                <p className="builders-section-subtitle">
                  Manage color collections and their item categories
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleCreateColorCollection}>
                  + Add New Color Collection
                </button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
              <select
                className="form-input"
                value={projectFilter}
                onChange={handleProjectFilterChange}
              >
                {projects.map(project => (
                  <option key={project._id} value={project._id}>{project.name}</option>
                ))}
              </select>

              <input
                type="text"
                className="form-input"
                placeholder="Search color collections..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {/* Color Collections Table */}
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Image</th>
                    <th>Project</th>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Categories</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {colorCollections.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="data-table-empty">
                        No color collections found. Click "+ Add New Color Collection" to create one.
                      </td>
                    </tr>
                  ) : (
                    colorCollections.map((collection, index) => (
                      <tr
                        key={collection._id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          opacity: draggedIndex === index ? 0.4 : 1,
                          backgroundColor: dragOverIndex === index ? '#F3F4F6' : undefined,
                          cursor: 'move'
                        }}
                      >
                        <td style={{ cursor: 'grab', padding: '8px' }}>
                          <GripVertical size={20} color="#9CA3AF" />
                        </td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }}>
                          {collection.heroImageUrl ? (
                            <img
                              src={collection.heroImageUrl}
                              alt={collection.name}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #E5E7EB'
                              }}
                            />
                          ) : (
                            <div style={{ width: '40px', height: '40px', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                              <Palette size={24} color="#6B7280" />
                            </div>
                          )}
                        </td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{collection.projectName || '-'}</td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{collection.name}</td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }}>{collection.order}</td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: collection.isActive ? '#DEF7EC' : '#FEE2E2',
                            color: collection.isActive ? '#03543F' : '#991B1B'
                          }}>
                            {collection.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }}>{collection.itemCategories?.length || 0}</td>
                        <td onClick={() => handleViewColorCollection(collection._id)} style={{ cursor: 'pointer' }}>{new Date(collection.updatedAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="builders-pagination" style={{ marginTop: '20px' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary btn-sm"
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="btn btn-secondary btn-sm"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Drawer for Create/Edit */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title={editColorCollectionId ? 'Edit Color Collection' : 'Add New Color Collection'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseDrawer} form="color-collection-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="color-collection-form">
                {editColorCollectionId ? 'Update' : 'Create'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && workspaceId && (
            <ColorCollectionFormSlider
              workspaceId={workspaceId}
              colorCollectionId={editColorCollectionId || undefined}
              projectId={projectFilter || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
            />
          )}
        </Drawer>

        {/* Color Collection Details Slider */}
        {viewColorCollectionId && workspaceId && (
          <ColorCollectionDetailsSlider
            workspaceId={workspaceId}
            colorCollectionId={viewColorCollectionId}
            onClose={handleCloseSlider}
            onSave={handleSaveColorCollection}
          />
        )}
      </>
    </AppLayout>
  );
};

export default ColorCollections;
